import React, {  Fragment, useMemo } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';

import { distances, colors, globalPageMaxWidth } from '../../../styles/constants';
import { NextButton, SmallMarketingButton, BackButton, MarketingInput } from '../../common/components';
import { Radiobutton } from '../../../components/Forms';
import Card from '../../../components/Card';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../../components/Table';
import { Campaign, AudienceType } from '../../types';

interface AudienceProps {
    values: Campaign;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    onNextStage: () => void;
    onPreviousStage: () => void;
    isEdit?: boolean;
}

interface Person {
    name: string;
    location: string;
}

function getLastName(name: string) {
    const indexOfSeparator = name.trim().lastIndexOf(' ');
    if (indexOfSeparator === -1) return name;
    return name.substring(indexOfSeparator + 1);
}

const groupBy = <K, >(list: K[], getGroupKey: (item: K) => string): Record<string, K[]> =>
    list.reduce((groups, item) => {
        const key = getGroupKey(item);
        groups[key] = groups[key] ?? [];
        groups[key].push(item);
        return groups;
    }, {} as Record<string, K[]>);

const groupAndSortPersons = (persons: Person[]): Record<string, Person[]> =>
    groupBy(
        [...persons].sort((a, b) => getLastName(a.name).localeCompare(getLastName(b.name))),
        (person) => getLastName(person.name).charAt(0).toLocaleLowerCase()
    );

const Audience = (
    {
        values,
        setFieldValue,
        onNextStage,
        onPreviousStage,
        isEdit,
    }: AudienceProps
) => {
    const { t } = useTranslation();

    const sortedAudience = useMemo(() => groupAndSortPersons(values.audience), [values.audience]);

    return (
        <>
            <Card
                title={t('marketing.campaign.select_audience')}
                subtitle={t('marketing.campaign.select_audiences_from_database')}
                shortHr
                marginBottom={distances.small}
            >
                <GroupItemsWrapper>
                    <Radiobutton
                        name={'values.audienceType'}
                        label={t('marketing.campaign.all_audience')}
                        value={values.audienceType === AudienceType.allAudience}
                        onChange={() => {
                            setFieldValue('audienceType', AudienceType.allAudience);
                            setFieldValue('audienceUserIds', []);
                        }}
                    />
                    <Radiobutton
                        name={'values.audienceType'}
                        label={t('marketing.campaign.all_audience_registered')}
                        value={values.audienceType === AudienceType.predefinedAudience}
                        onChange={() => {
                            setFieldValue('audienceType', AudienceType.predefinedAudience);
                            setFieldValue('audienceUserIds', []);
                        }}
                    />
                    <Radiobutton
                        name={'values.audienceType'}
                        label={t('marketing.campaign.audience_user_ids')}
                        value={values.audienceType === AudienceType.userIds}
                        onChange={() => {
                            setFieldValue('audienceType', AudienceType.userIds);
                            setFieldValue('audienceUserIds', []);
                        }}
                    />
                </GroupItemsWrapper>
                {values.audienceType === AudienceType.userIds && (
                    <Fragment>
                        {<MarketingInput
                            type="text"
                            label={t('marketing.campaign.audience_user_ids_label')}
                            labelOpacity={1}
                            placeholder=""
                            name={`audienceUserIds`}
                            onChange={(e) => {
                                const userIds = e.currentTarget.value.split(',');
                                setFieldValue('audienceUserIds', userIds);
                            }}
                            onBlur={(e) => {
                                const userIds = e.currentTarget.value.split(',').filter(x => x).map(id => id.trim());
                                setFieldValue('audienceUserIds', userIds);
                            }}
                            value={values.audienceUserIds.join(', ')}
                        />}
                    </Fragment>
                )}
            </Card>
            {!isEdit && (
                <NavigationButtonsWrapper>
                    <BackButton type="button" onClick={onPreviousStage}>
                        {t('marketing.campaign.back')}
                    </BackButton>
                    <NextButton type="submit" onClick={onNextStage}>
                        {t('marketing.campaign.next')}
                    </NextButton>
                </NavigationButtonsWrapper>
            )}
        </>
    );
};
const GroupItemsWrapper = styled.div`
    display: flex;
    width: 75%;
    justify-content: space-between;
    margin: ${distances.normal} 0;

    > *:first-child {
        margin-right: ${distances.small};
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const LetterRow = styled(Tr as any)`
    position: absolute;
    width: 100%;
    left: 0;
    background: ${colors.backgroundAlt};
    padding: 0 ${distances.normal};
`;

const LetterCell = styled(Td as any)`
    padding: ${distances.tiny} 0;
`;

const LetterCircle = styled.span`
    height: ${distances.normal};
    width: ${distances.normal};
    background-color: ${colors.text};
    color: ${colors.background};
    border-radius: 50%;
    display: inline-block;
    text-align: center;
    line-height: 225%;
`;

const MoreRow = styled(Td as any)`
    text-align: end;
`;

const MoreButton = styled.button`
    color: ${colors.primary};
    background: none;
    border: none;
    outline: none;
    cursor: pointer;
`;

const NavigationButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    max-width: ${globalPageMaxWidth}px;
`;

export default Audience;
